import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { API } from '../../API';
import { Loader } from '../../components/Loader/Loader';
import Modal from '../../components/Modal';
import { SchemaForm } from './components/SchemaForm';
import { ResultDisplay } from './components/ResultDisplay';
import { RunHistory } from './components/RunHistory';
import { CancelOutcome, CancelRunButton } from './components/CancelRunButton';
import { RunInputs } from './components/RunInputs';
import { useWindmillExecution } from './hooks/useWindmillExecution';
import { WindmillApp } from './types';

import styles from './WindmillAppPage.module.scss';

export function WindmillAppPage(): React.ReactElement | null {
  const { t } = useTranslation();
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const executionParam = searchParams.get('execution');
  const [app, setApp] = useState<WindmillApp | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [runningLong, setRunningLong] = useState(false);

  const {
    run,
    resume,
    applyExecution,
    executionId,
    status,
    input,
    output,
    error,
    isRunning,
    durationMs,
    completedAt,
    reset,
  } = useWindmillExecution(app);

  // The `execution` query param is the handle to the run being displayed: it
  // survives a reload or a closed tab, which async runs (hours long) need.
  const setExecutionParam = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id) {
            next.set('execution', id);
          } else {
            next.delete('execution');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Deep link / reload / history row: attach to the run named in the URL.
  useEffect(() => {
    if (!app || !executionParam) return;
    if (executionParam === executionId) return;
    resume(executionParam);
  }, [app, executionParam, executionId, resume]);

  // A run may be cancelled from the page or from a history row; only the one
  // currently on screen needs its state re-synced.
  const handleCancelSettled = useCallback(
    (id: string, outcome: CancelOutcome) => {
      if (id !== executionId) return;
      if (outcome.kind === 'canceled') {
        applyExecution(outcome.execution);
      } else {
        // It finished on its own while the user was clicking — read the real
        // state back instead of guessing at it.
        resume(id);
      }
    },
    [applyExecution, executionId, resume],
  );

  const handleReset = useCallback(() => {
    reset();
    setExecutionParam(null);
  }, [reset, setExecutionParam]);

  const fetchApp = useCallback(() => {
    if (!appId) return;
    API.fetch('GET', `/API/v2/windmill/apps/${appId}`)
      .then((data: WindmillApp) => {
        setApp(data);
        setLoadError(null);
      })
      .catch((err: Error) => {
        setLoadError(err.message);
      });
  }, [appId]);

  useEffect(fetchApp, [fetchApp]);

  // Show "taking a moment" message after 3s of running
  useEffect(() => {
    if (!isRunning) {
      setRunningLong(false);
      return;
    }
    const timer = setTimeout(() => setRunningLong(true), 3000);
    return () => clearTimeout(timer);
  }, [isRunning]);

  const handleSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setServerErrors({});
      setExecutionParam(null);
      try {
        setExecutionParam(await run(values));
      } catch (err: unknown) {
        if (err instanceof Error) {
          toast.error(err.message);
        }
      }
    },
    [run, setExecutionParam],
  );

  const handleRunNoInput = useCallback(async () => {
    setShowConfirmModal(false);
    setServerErrors({});
    setExecutionParam(null);
    try {
      setExecutionParam(await run({}));
    } catch (err: unknown) {
      if (err instanceof Error) {
        toast.error(err.message);
      }
    }
  }, [run, setExecutionParam]);

  if (!app && !loadError) {
    return <Loader />;
  }

  if (loadError) {
    return (
      <div className={styles.root}>
        <div className={styles.errorState}>
          <p>{loadError}</p>
          <button
            className={styles.backButton}
            onClick={() => navigate('/windmill-apps')}
          >
            {t('Back to Apps')}
          </button>
        </div>
      </div>
    );
  }

  if (!app) return null;

  const hasProperties =
    app.input_schema?.properties &&
    Object.keys(app.input_schema.properties).length > 0;

  return (
    <div className={styles.root}>
      <button
        className={styles.backLink}
        onClick={() => navigate('/windmill-apps')}
      >
        &larr; {t('Back to Apps')}
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {app.icon && <span className={styles.appIcon}>{app.icon}</span>}
          <div>
            <h1 className={styles.appName}>{app.name}</h1>
            {app.description && (
              <p className={styles.appDescription}>{app.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>
        {hasProperties ? (
          <div className={styles.formSection}>
            <SchemaForm
              schema={app.input_schema}
              fieldOrder={app.field_order}
              onSubmit={handleSubmit}
              isSubmitting={isRunning}
              serverErrors={serverErrors}
            />
          </div>
        ) : (
          <div className={styles.noInputSection}>
            <p className={styles.noInputText}>
              {t('This app requires no input parameters.')}
            </p>
            <button
              className={styles.runButton}
              onClick={() => setShowConfirmModal(true)}
              disabled={isRunning}
            >
              {isRunning ? t('Running...') : t('Run')}
            </button>
          </div>
        )}

        {isRunning && (
          <div className={styles.runningIndicator}>
            <div className={styles.spinner} />
            <span>
              {runningLong
                ? t('Running... this may take a moment')
                : t('Running...')}
            </span>
            {executionId && (
              <CancelRunButton
                executionId={executionId}
                onSettled={handleCancelSettled}
              />
            )}
          </div>
        )}

        {status && <RunInputs input={input} schema={app.input_schema} />}

        {status && !isRunning && (
          <ResultDisplay
            status={status}
            output={output}
            error={error}
            durationMs={durationMs}
            completedAt={completedAt}
            resultDisplay={app.result_display}
            onDismissError={handleReset}
          />
        )}

        <RunHistory
          appId={app.id}
          activeExecutionId={executionParam}
          activeStatus={status}
          onSelect={setExecutionParam}
          onCancelSettled={handleCancelSettled}
        />
      </div>

      {showConfirmModal && (
        <Modal
          isVisible={showConfirmModal}
          title={t('Run App')}
          content={t('Are you sure you want to run this app?')}
          footer={
            <div className={styles.modalFooter}>
              <button
                className={styles.modalCancel}
                onClick={() => setShowConfirmModal(false)}
              >
                {t('Cancel')}
              </button>
              <button className={styles.modalConfirm} onClick={handleRunNoInput}>
                {t('Run')}
              </button>
            </div>
          }
          onClose={() => setShowConfirmModal(false)}
          className={undefined}
        />
      )}
    </div>
  );
}
